/**
 * 🟦 V11.5 — Import Recipe.
 *
 * Valida un payload portable (de archivo o URL share) y lo inyecta en el
 * recipesStore local con un nuevo id, asociado al appId actual.
 */
import { addRecipe } from '../recipes/recipesStore';

/**
 * Valida el shape de un payload portable. Devuelve {ok, error?, portable?}.
 * Reglas:
 *  - version requerido (string)
 *  - name requerido
 *  - nodeType requerido
 *  - steps[] no vacío, cada step con patch[] no vacío
 *  - cada patch op con `op` ∈ {add,replace,remove} y `path` empezando con /
 */
export function validatePortableRecipe(raw) {
  if (!raw || typeof raw !== 'object') return { ok: false, error: 'invalid_payload' };
  if (typeof raw.version !== 'string') return { ok: false, error: 'missing_version' };
  if (!raw.name || typeof raw.name !== 'string') return { ok: false, error: 'missing_name' };
  if (!raw.nodeType || typeof raw.nodeType !== 'string') return { ok: false, error: 'missing_nodeType' };
  if (!Array.isArray(raw.steps) || raw.steps.length === 0) return { ok: false, error: 'no_steps' };
  for (const s of raw.steps) {
    if (!s || !Array.isArray(s.patch) || s.patch.length === 0) {
      return { ok: false, error: 'invalid_step' };
    }
    for (const op of s.patch) {
      if (!op || typeof op !== 'object') return { ok: false, error: 'invalid_patch_op' };
      if (!['add', 'replace', 'remove'].includes(op.op)) return { ok: false, error: 'invalid_op_type' };
      if (typeof op.path !== 'string' || !op.path.startsWith('/')) return { ok: false, error: 'invalid_op_path' };
    }
  }
  return { ok: true, portable: raw };
}

/**
 * Importa una receta validada al recipesStore local.
 * Devuelve {ok, recipe?, error?}.
 */
export function importRecipeToStore(portable, appId) {
  if (!appId) return { ok: false, error: 'missing_appId' };
  const v = validatePortableRecipe(portable);
  if (!v.ok) return v;
  const saved = addRecipe({
    appId,
    name: portable.name,
    description: portable.description || '',
    nodeType: portable.nodeType,
    steps: portable.steps,
  });
  if (!saved) return { ok: false, error: 'store_rejected' };
  return { ok: true, recipe: saved };
}

/**
 * Lee un File (input type=file) y devuelve el JSON parseado. Async.
 */
export function readRecipeFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('no_file'));
    if (file.size > 512 * 1024) return reject(new Error('file_too_large'));
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read_error'));
    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        const parsed = JSON.parse(text);
        resolve(parsed);
      } catch (e) {
        reject(new Error('invalid_json'));
      }
    };
    reader.readAsText(file);
  });
}

/**
 * Abre un file picker programático (<input type=file>) y resuelve con el JSON.
 * Cancela si el usuario no elige archivo.
 */
export function openRecipeFilePicker() {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.dxa-recipe.json,application/json';
    input.style.display = 'none';
    input.onchange = async (e) => {
      const file = e.target.files && e.target.files[0];
      document.body.removeChild(input);
      if (!file) return reject(new Error('cancelled'));
      try {
        const json = await readRecipeFile(file);
        resolve(json);
      } catch (err) {
        reject(err);
      }
    };
    document.body.appendChild(input);
    input.click();
  });
}
